/**
 * Watches processing for EVERY upload in the store — mounted once at the router
 * root, under `RealtimeRelayProvider` and the query client.
 *
 * It subscribes to the relay's `attachment.processed` / `transcript.ready` /
 * `transcript.failed` events on each in-flight attachment's channel (the bare
 * attachmentId, or `public:<attachmentId>` for a signed-out upload — see
 * `_lib/processing-announcements.ts`) and folds the terminal outcome into the
 * store when media announces it. Events are matched to jobs by the payload's
 * attachmentId, never by channel name. The subscription starts as soon as
 * `/init` assigns the attachmentId (before `/final` can start processing), so
 * there is no missed-event window. If the relay connection errors while jobs
 * are in flight, ONE reconcile fetch of `/uploads/status` replaces the lost
 * events — a recovery, not a poll loop.
 *
 * Living at the root rather than in the uploading component is what lets an
 * upload outlive its starter: a photo picked in a dialog that has since closed
 * still gets its `processed` event, and every list on screen that was showing
 * the unrenderable original (a HEIC before its webp exists) is refetched the
 * moment the rendition is announced.
 */

import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { env } from "@/env";
import { useRealtimeRelay } from "@/features/shared/realtime-relay";
import { fetchStatus } from "./_api/media-v3-client";
import {
  ANALYSIS_WAIT_MS,
  CLERK_TOKEN_TEMPLATE,
} from "./_hooks/use-media-upload";
import {
  activeAttachmentIdsOf,
  activeChannelsOf,
  foldProcessed,
} from "./_lib/processing-announcements";
import { queryHoldsAny } from "./_lib/query-holds";
import { applyUploadEvent, type UploadEvent } from "./_lib/upload-pipeline";
import { useUploadStore } from "./_lib/upload-store";
import type { StatusItem } from "./_types/media-upload";

/**
 * How long processed ids are batched before the lists holding them refetch. A
 * gallery drop processes many files seconds apart; one refetch per burst beats
 * one per file.
 */
export const REFETCH_BATCH_MS = 800;

export function MediaUploadTracker() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const entries = useUploadStore((state) => state.entries);

  /** Fold a terminal outcome into every job of that attachment (realtime + recovery). */
  const applyProcessed = useCallback((item: StatusItem) => {
    const store = useUploadStore.getState();
    for (const entry of store.entries) {
      const { job } = entry;
      if (job.attachmentId !== item.attachmentId) continue;
      if (job.phase === "done" || job.phase === "failed") continue;
      const next = foldProcessed(job, item);
      store.patchJob(job.id, () => next);
      if (item.status === "UPLOADED") entry.options?.onUploaded?.(next);
    }
  }, []);

  /** Fold an analysis outcome into every job of that attachment, whatever its phase. */
  const applyAnalysis = useCallback(
    (attachmentId: string, event: UploadEvent) => {
      const store = useUploadStore.getState();
      for (const { job } of store.entries) {
        if (job.attachmentId !== attachmentId) continue;
        store.patchJob(job.id, (current) => applyUploadEvent(current, event));
      }
    },
    [],
  );

  // Every list on screen that holds a just-processed attachment is showing a
  // URL the media service has retired — refetch it. Batched per burst.
  const pendingRefetch = useRef(new Set<string>());
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefetch = useCallback(
    (attachmentId: string) => {
      pendingRefetch.current.add(attachmentId);
      if (refetchTimer.current) return;
      refetchTimer.current = setTimeout(() => {
        refetchTimer.current = null;
        const ids = [...pendingRefetch.current];
        pendingRefetch.current.clear();
        void queryClient.invalidateQueries({
          predicate: (query) => queryHoldsAny(query.state.data, ids),
        });
      }, REFETCH_BATCH_MS);
    },
    [queryClient],
  );
  useEffect(() => {
    return () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    };
  }, []);

  const activeChannels = useMemo(() => activeChannelsOf(entries), [entries]);
  const { status: realtimeStatus } = useRealtimeRelay({
    channels: activeChannels,
    events: ["attachment.processed", "transcript.ready", "transcript.failed"],
    // An upload's outcome runs its follow-up (`onUploaded`) even in a background tab. The tab's
    // stream is shared, so the other listeners of these uploads (Birdie chat, voice capture) hear
    // them live too.
    liveWhileHidden: true,
    onData(payload) {
      if (payload.event === "attachment.processed") {
        const data = payload.data;
        applyProcessed({
          attachmentId: data.attachmentId,
          uploaded: data.status === "UPLOADED",
          status: data.status,
          contentUri: data.contentUri ?? null,
          thumbnailUri: data.thumbnailUri ?? null,
          aiAnalyzed: Boolean(data.aiAnalyzed),
          contentType: data.contentType ?? null,
          fileSize: data.fileSize ?? null,
        });
        if (data.status === "UPLOADED") scheduleRefetch(data.attachmentId);
        // Nothing was analyzed (a plain FILE) or processing failed outright —
        // no text is coming, so settle the analysis instead of hanging on it.
        if (!data.aiAnalyzed || data.status === "FAILED") {
          applyAnalysis(data.attachmentId, { type: "analysis-none" });
        }
        return;
      }
      if (payload.event === "transcript.ready") {
        applyAnalysis(payload.data.attachmentId, {
          type: "analysis-ready",
          text: payload.data.text,
          model: payload.data.model ?? null,
        });
        return;
      }
      applyAnalysis(payload.data.attachmentId, {
        type: "analysis-failed",
        reason: payload.data.reason || "analysis failed",
      });
    },
  });

  // Recovery: a relay connection error while jobs are in flight gets ONE
  // reconcile fetch to replace the lost events — never a poll loop. It asks
  // about attachmentIds, never channel names.
  const attachmentIdsKey = useMemo(
    () => activeAttachmentIdsOf(entries).join(","),
    [entries],
  );
  useEffect(() => {
    if (realtimeStatus !== "error" || attachmentIdsKey === "") return;
    let cancelled = false;
    void (async () => {
      const token = await getToken({ template: CLERK_TOKEN_TEMPLATE });
      const items = await fetchStatus(
        { baseUrl: env.PUBLIC_MEDIA_SERVICE_URL, token: token ?? "" },
        attachmentIdsKey.split(","),
      ).catch(() => [] as StatusItem[]);
      if (cancelled) return;
      for (const item of items) {
        if (item.status !== "UPLOADED" && item.status !== "FAILED") continue;
        applyProcessed(item);
        if (item.status === "UPLOADED") scheduleRefetch(item.attachmentId);
        // The status endpoint carries the stored AI text; a job reconciled
        // before the text exists settles to `none` so no consumer waits forever.
        applyAnalysis(
          item.attachmentId,
          item.aiAnalysis
            ? {
                type: "analysis-ready",
                text: item.aiAnalysis.text,
                model: item.aiAnalysis.model,
              }
            : { type: "analysis-none" },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    realtimeStatus,
    attachmentIdsKey,
    applyProcessed,
    applyAnalysis,
    scheduleRefetch,
    getToken,
  ]);

  // A done job whose AI text never arrives settles after ANALYSIS_WAIT_MS — one
  // timer per attachment, started the moment `attachment.processed` lands.
  const analysisTimers = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  useEffect(() => {
    const timers = analysisTimers.current;
    const waitingIds = new Set<string>();
    for (const { job } of entries) {
      if (!job.attachmentId) continue;
      if (job.phase === "done" && job.analysis.status === "pending") {
        waitingIds.add(job.attachmentId);
      }
    }
    for (const id of waitingIds) {
      if (timers.has(id)) continue;
      timers.set(
        id,
        setTimeout(() => {
          timers.delete(id);
          applyAnalysis(id, { type: "analysis-none" });
        }, ANALYSIS_WAIT_MS),
      );
    }
    for (const [id, timer] of timers) {
      if (waitingIds.has(id)) continue;
      clearTimeout(timer);
      timers.delete(id);
    }
  }, [entries, applyAnalysis]);
  useEffect(() => {
    const timers = analysisTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  // Orphaned entries (their hook is gone) leave once nothing more can happen.
  useEffect(() => {
    useUploadStore.getState().prune();
  }, [entries]);

  return null;
}
